import type { BillingCycle, Subscription } from "../types";

export type PaymentOccurrence = {
  id: string;
  subscription: Subscription;
  date: string;
  amount: number;
};

export const categories = [
  "エンタメ",
  "音楽",
  "AI / 仕事",
  "クリエイティブ",
  "食料品",
  "ニュース",
  "読書",
  "ストレージ",
  "開発",
  "ショッピング",
] as const;

export const categoryColors: Record<string, string> = {
  "エンタメ": "#7C5CFF",
  "音楽": "#7CE0B8",
  "AI / 仕事": "#FFB86B",
  "ストレージ": "#7BB7FF",
  "クリエイティブ": "#FF6B9D",
  "ニュース": "#E5E5E5",
  "食料品": "#A0E37C",
  "開発": "#8B949E",
  "ショッピング": "#FFD166",
  "読書": "#5EC9F5",
};

export const servicePalette = [
  "#E50914",
  "#1ED760",
  "#10A37F",
  "#7BB7FF",
  "#FA0F00",
  "#C8102E",
  "#84BD00",
  "#8B949E",
  "#FF9900",
  "#FF0033",
  "#0061FF",
  "#7C5CFF",
];

export function yen(value: number) {
  return "¥" + Math.round(value).toLocaleString("ja-JP");
}

export function parseYenInput(value: string) {
  const normalized = value.replace(/,/g, "").trim();
  if (!/^\d+$/.test(normalized)) return null;
  const parsed = Number(normalized);
  if (!Number.isSafeInteger(parsed) || parsed < 1) return null;
  return parsed;
}

export function monthlyOf(input: Pick<Subscription, "price" | "cycle">) {
  if (input.cycle === "weekly") return Math.round(input.price * 4.345);
  if (input.cycle === "yearly") return Math.round(input.price / 12);
  return Math.round(input.price);
}

export function cycleLabel(cycle: BillingCycle) {
  if (cycle === "weekly") return "週次";
  if (cycle === "yearly") return "年次";
  return "月次";
}

export function cycleLabelShort(cycle: BillingCycle) {
  if (cycle === "weekly") return "weekly";
  if (cycle === "yearly") return "yearly";
  return "monthly";
}

export function totalMonthly(subscriptions: Subscription[]) {
  return subscriptions.reduce((total, sub) => total + monthlyOf(sub), 0);
}

export function categoryTotals(subscriptions: Subscription[]) {
  const map = new Map<string, number>();
  subscriptions.forEach((sub) => {
    map.set(sub.category, (map.get(sub.category) ?? 0) + monthlyOf(sub));
  });
  return [...map.entries()]
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
}

export function upcomingSubscriptions(subscriptions: Subscription[], today = new Date(), days = 60) {
  const seen = new Set<string>();
  return upcomingPayments(subscriptions, today, days)
    .filter((occurrence) => {
      if (seen.has(occurrence.subscription.id)) return false;
      seen.add(occurrence.subscription.id);
      return true;
    })
    .map((occurrence) => occurrence.subscription);
}

export function upcomingPayments(subscriptions: Subscription[], today = new Date(), days = 60) {
  const from = startOfLocalDay(today);
  const to = addDays(from, days);
  return paymentOccurrencesInRange(subscriptions, from, to);
}

export function daysUntil(isoDate: string, today = new Date()) {
  const start = startOfLocalDay(today).getTime();
  const target = toDateOnly(isoDate).getTime();
  return Math.round((target - start) / 86_400_000);
}

export function toDateOnly(isoDate: string) {
  const date = parseDateOnlyStrict(isoDate);
  if (!date) throw new Error(`Invalid date-only value: ${isoDate}`);
  return date;
}

export function startOfLocalDay(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function parseDateOnlyStrict(value: string) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (year < 1000 || year > 9999) return null;
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > daysInMonth(year, month - 1)) return null;

  return new Date(year, month - 1, day);
}

export function isValidDateOnly(value: string) {
  return parseDateOnlyStrict(value) !== null;
}

export function formatDateOnly(date: Date) {
  const clean = startOfLocalDay(date);
  const y = clean.getFullYear();
  const m = String(clean.getMonth() + 1).padStart(2, "0");
  const d = String(clean.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function addDays(date: Date, days: number) {
  const clean = startOfLocalDay(date);
  clean.setDate(clean.getDate() + days);
  return clean;
}

export function advancePaymentDate(date: Date, cycle: BillingCycle, anchor = date) {
  const current = startOfLocalDay(date);
  const anchorDay = anchor.getDate();
  const anchorMonth = anchor.getMonth();

  if (cycle === "weekly") return addDays(current, 7);

  if (cycle === "monthly") {
    const targetYear = current.getFullYear();
    const targetMonth = current.getMonth() + 1;
    return clampedDate(targetYear, targetMonth, anchorDay);
  }

  return clampedDate(current.getFullYear() + 1, anchorMonth, anchorDay);
}

export function firstPaymentDateOnOrAfter(anchor: Date, cycle: BillingCycle, target: Date) {
  const cleanAnchor = startOfLocalDay(anchor);
  const cleanTarget = startOfLocalDay(target);
  if (cleanAnchor.getTime() >= cleanTarget.getTime()) return cleanAnchor;

  if (cycle === "weekly") {
    const elapsedDays = Math.floor((cleanTarget.getTime() - cleanAnchor.getTime()) / 86_400_000);
    const elapsedWeeks = Math.max(0, Math.floor(elapsedDays / 7));
    let candidate = addDays(cleanAnchor, elapsedWeeks * 7);
    if (candidate.getTime() < cleanTarget.getTime()) candidate = advancePaymentDate(candidate, cycle, cleanAnchor);
    return candidate;
  }

  if (cycle === "monthly") {
    const elapsedMonths =
      (cleanTarget.getFullYear() - cleanAnchor.getFullYear()) * 12 + (cleanTarget.getMonth() - cleanAnchor.getMonth());
    let candidate = clampedDate(cleanAnchor.getFullYear(), cleanAnchor.getMonth() + Math.max(0, elapsedMonths), cleanAnchor.getDate());
    if (candidate.getTime() < cleanTarget.getTime()) candidate = advancePaymentDate(candidate, cycle, cleanAnchor);
    return candidate;
  }

  const elapsedYears = cleanTarget.getFullYear() - cleanAnchor.getFullYear();
  let candidate = clampedDate(cleanAnchor.getFullYear() + Math.max(0, elapsedYears), cleanAnchor.getMonth(), cleanAnchor.getDate());
  if (candidate.getTime() < cleanTarget.getTime()) candidate = advancePaymentDate(candidate, cycle, cleanAnchor);
  return candidate;
}

export function getNextPaymentDate(subscription: Subscription, today = new Date()) {
  const anchor = toDateOnly(subscription.nextDate);
  const start = startOfLocalDay(today);
  const candidate = firstPaymentDateOnOrAfter(anchor, subscription.cycle, start);
  return formatDateOnly(candidate);
}

export function paymentOccurrencesInRange(subscriptions: Subscription[], from: Date | string, to: Date | string): PaymentOccurrence[] {
  const start = typeof from === "string" ? toDateOnly(from) : startOfLocalDay(from);
  const end = typeof to === "string" ? toDateOnly(to) : startOfLocalDay(to);
  if (end.getTime() < start.getTime()) return [];

  const occurrences: PaymentOccurrence[] = [];

  subscriptions.forEach((subscription) => {
    const anchor = parseDateOnlyStrict(subscription.nextDate);
    if (!anchor) return;

    let candidate = firstPaymentDateOnOrAfter(anchor, subscription.cycle, start);

    while (candidate.getTime() <= end.getTime()) {
      const date = formatDateOnly(candidate);
      occurrences.push({
        id: `${subscription.id}:${date}`,
        subscription,
        date,
        amount: subscription.price,
      });
      candidate = advancePaymentDate(candidate, subscription.cycle, anchor);
    }
  });

  return occurrences.sort((a, b) => a.date.localeCompare(b.date) || a.subscription.name.localeCompare(b.subscription.name, "ja"));
}

export function monthlyPaymentTrend(subscriptions: Subscription[], today = new Date(), months = 6) {
  const currentMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  return Array.from({ length: months }, (_, index) => {
    const offset = index - months + 1;
    const monthStart = new Date(currentMonthStart.getFullYear(), currentMonthStart.getMonth() + offset, 1);
    const monthEnd = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0);
    const occurrences = paymentOccurrencesInRange(subscriptions, monthStart, monthEnd);
    return {
      label: monthStart.toLocaleDateString("ja-JP", { month: "short" }),
      value: occurrences.reduce((sum, occurrence) => sum + occurrence.amount, 0),
    };
  });
}

export function fmtDate(isoDate: string) {
  const date = toDateOnly(isoDate);
  return `${date.getMonth() + 1}/${date.getDate()}`;
}

export function fmtDateLong(isoDate: string) {
  const date = toDateOnly(isoDate);
  const week = ["日", "月", "火", "水", "木", "金", "土"][date.getDay()];
  return `${date.getMonth() + 1}月${date.getDate()}日 (${week})`;
}

export function todayInputValue(date = new Date()) {
  return formatDateOnly(date);
}

export function markForName(name: string) {
  const trimmed = name.trim();
  if (!trimmed) return "?";
  return trimmed.slice(0, 1).toUpperCase();
}

export function colorForName(name: string) {
  const seed = [...name].reduce((sum, char) => sum + char.charCodeAt(0), 0);
  return servicePalette[seed % servicePalette.length];
}

export function makeId() {
  if (crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function daysInMonth(year: number, monthIndex: number) {
  return new Date(year, monthIndex + 1, 0).getDate();
}

function clampedDate(year: number, monthIndex: number, day: number) {
  const lastDay = daysInMonth(year, monthIndex);
  return new Date(year, monthIndex, Math.min(day, lastDay));
}
